import Box, { BoxProps } from '@material-ui/core/Box';
import Divider from '@material-ui/core/Divider';
import { makeStyles } from '@material-ui/core/styles';
import Tab from '@material-ui/core/Tab';
import Tabs from '@material-ui/core/Tabs';
import Typography from '@material-ui/core/Typography';
import * as React from 'react';
import { connect } from 'react-redux';

import Layers from '../components/Layers';
import Search from '../components/Search';
import { SearchField } from '../models/SearchField';
import {
  addSearchField,
  clearSearchFields,
  modifySearchField,
  removeSearchField,
  replaceSearchField,
  setSidebarTab,
  setToggleableLayer
} from '../store/actions';
import { State } from '../store/reducer';
import {
  getCompanyOptions,
  getCurrentSidebarTab,
  getFilteredAgreementsNumber,
  getSearchFields,
  getToggleableLayers,
  getTotalAgreementsNumber
} from '../store/selectors';
import SidebarHeader from './SidebarHeader';

export interface StateProps {
  currentTab: number;
  searchFields: any[];
  companyOptions: string[];
  toggleableLayers: any[];
  filteredAgreementsNumber: number;
  totalAgreementsNumber: number;
}

export interface DispatchProps {
  onTabClick: (index: number) => void;
  onChangeField: (searchField: Partial<SearchField>) => void;
  onNewField: (newField: SearchField) => void;
  onClearFields: () => void;
  onRemoveField: (id: string) => void;
  onReplaceField: (newSearchField: SearchField) => void;
  onToggleLayer: (id: string) => void;
}

export interface OwnProps {
  flex?: string;
  height?: number;
  width?: number;
}

type Props = StateProps & DispatchProps & OwnProps;

export interface TabPanelProps extends BoxProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const useStyles = makeStyles(theme => ({
  root: {
    backgroundColor: theme.palette.background.paper
  },
  avatar: {
    height: 52,
    width: 52,
    marginLeft: theme.spacing(3)
  },
  tabs: {
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1)
  },
  tab: {
    minWidth: 'auto',
    textTransform: 'none',
    ...theme.typography.subtitle1
  },
  tabIndicator: {
    display: 'none'
  }
}));

function TabPanel({ children, value, index, ...boxProps }: TabPanelProps) {
  return (
    <Box
      {...boxProps}
      component="div"
      role="tabpanel"
      flexDirection="column"
      display={value !== index ? 'none' : 'flex'}
      id={`sidebar-tabpanel-${index}`}
      aria-labelledby={`sidebar-tab-${index}`}
      overflow="hidden"
    >
      {children}
    </Box>
  );
}

function a11yProps(index: any) {
  return {
    id: `sidebar-tab-${index}`,
    'aria-controls': `sidebar-tabpanel-${index}`
  };
}

export const Sidebar: React.FC<Props> = ({
  currentTab,
  companyOptions,
  onTabClick,
  onChangeField,
  onClearFields,
  onRemoveField,
  onReplaceField,
  onToggleLayer,
  onNewField,
  searchFields,
  toggleableLayers,
  filteredAgreementsNumber,
  totalAgreementsNumber,
  ...boxProps
}) => {
  const classes = useStyles();

  return (
    <Box
      component="section"
      display="flex"
      flexDirection="column"
      bgcolor="background.paper"
      {...boxProps}
    >
      <SidebarHeader flex="0 0 auto" />
      <Box flex="0 0 auto">
        <Tabs
          value={currentTab}
          textColor="primary"
          aria-label="Sidebar tabs"
          classes={{ root: classes.tabs, indicator: classes.tabIndicator }}
        >
          <Tab
            label="Agreements"
            className={classes.tab}
            onClick={onTabClick.bind(null, 0)}
            {...a11yProps(0)}
          />
          <Tab
            label="Layers"
            className={classes.tab}
            onClick={onTabClick.bind(null, 1)}
            {...a11yProps(1)}
          />
        </Tabs>
      </Box>
      <Divider />
      <TabPanel value={currentTab} index={0} flex="1 1 0%">
        <Box flex="0 0 auto" mt={2} mx={2}>
          <Typography>{`${filteredAgreementsNumber}/${totalAgreementsNumber} Agreements`}</Typography>
        </Box>
        <Search
          flex="1 1 0%"
          onChangeField={onChangeField}
          onClearFields={onClearFields}
          onRemoveField={onRemoveField}
          onReplaceField={onReplaceField}
          onNewField={onNewField}
          searchFields={searchFields}
          companyOptions={companyOptions}
        />
      </TabPanel>
      <TabPanel value={currentTab} index={1} flex="1 1 0%">
        <Layers layers={toggleableLayers} toggleLayer={onToggleLayer} flex="1 1 0%" />
      </TabPanel>
    </Box>
  );
};

const mapState = (state: State): StateProps => {
  return {
    currentTab: getCurrentSidebarTab(state),
    searchFields: getSearchFields(state),
    companyOptions: getCompanyOptions(state),
    toggleableLayers: getToggleableLayers(state),
    filteredAgreementsNumber: getFilteredAgreementsNumber(state),
    totalAgreementsNumber: getTotalAgreementsNumber(state)
  };
};

const mapDispatch = {
  onTabClick: setSidebarTab,
  onChangeField: modifySearchField,
  onClearFields: clearSearchFields,
  onNewField: addSearchField,
  onRemoveField: removeSearchField,
  onReplaceField: replaceSearchField,
  onToggleLayer: setToggleableLayer
};

export default connect<StateProps, DispatchProps, {}, State>(
  mapState,
  mapDispatch
)(Sidebar);
